import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  message,
  Modal,
  Card,
  Row,
  Col,
  Upload,
  Button,
  Form,
  Input,
  Pagination,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Status } from '../helpers/consts/status';
import {
  fetchLogos,
  addLogo,
  removeLogo,
} from '../store/actions/logos-actions';
import { fetchCarHouseLogos } from '../store/actions/carHouseLogos-actions';
import Spinner from '../components/UI/Spinner/Spinner';
import PageHeader from '../components/UI/PageHeader/PageHeader';
import { assignLogoToUser, deleteLogoFromUser } from '../lib/userLogos';
import { getSelf } from '../store/actions/auth-actions';
import { useTranslation } from 'react-i18next';

const Logos = () => {
  const { confirm } = Modal;
  const { status, logos } = useSelector((state) => state.logos);
  const { loggedInUser } = useSelector((state) => state.auth);
  const { isLoading, carHouseLogos } = useSelector(
    (state) => state.carHouseLogos
  );
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    if (
      loggedInUser?.role?.name === 'carHouseAdmin' ||
      loggedInUser?.role?.name === 'user'
    ) {
      dispatch(fetchCarHouseLogos({ carHouseId: loggedInUser.carHouseId }));
    } else {
      dispatch(fetchLogos());
      if (status === Status.IDLE && !logos.length) {
        dispatch(fetchLogos());
      }
    }
  }, [dispatch]);

  const handleSubmitLogo = () => {
    form
      .validateFields()
      .then((values) => {
        const imageFile = values.logoFile?.[0]?.originFileObj;
        const name = values.name;

        if (!imageFile) {
          message.error(t('common.logoFileRequired')); 
          return;
        }

        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('name', name);

        dispatch(addLogo(formData));
        setOpenModal(false);
        message.success(t('common.logoUploadSuccess')); 
      })
      .catch(() => {
        message.error(t('common.formError')); 
      });
  };

  const handleRemoveUserLogo = async () => {
    try {
      await deleteLogoFromUser(loggedInUser.id, loggedInUser.logoId);
      message.success(t('common.logoRemoveSuccessfully')); 
      dispatch(getSelf(loggedInUser.id));
    } catch (error) {
      message.error(t('common.failedToRemoveLogo')); 
    }
  };

  const showConfirmDeleteLogo = (logo) => {
    confirm({
      title: `${t('common.deleteQuestion')} ${t('common.logo')}`, 
      icon: (
        <ExclamationCircleOutlined
          style={{ color: 'var(--ant-error-color)' }}
        />
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          <img
            src={logo.path}
            alt={logo.name}
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'contain',
              marginBottom: '10px',
            }}
          />
        </div>
      ),
      okType: 'danger',
      okText: t('common.yes'), 
      cancelText: t('common.no'), 
      maskClosable: true,
      onOk() {
        deleteLogoHandler(logo.id);
      },
      onCancel() {},
    });
  };

  const deleteLogoHandler = async (logoId) => {
    try {
      await dispatch(removeLogo(logoId));
      message.success(t('common.logoDeleteSuccess')); 
    } catch (err) {
      message.error(t('common.failedToRemoveLogo')); 
    }
  };

  const renderTopLogo = () => {
    if (loggedInUser?.role?.name !== 'root') {
      const hasLogo = loggedInUser?.logo?.path;

      return (
        <Card
          style={{
            width: '100%',
            maxWidth: 240,
            margin: '20px 0',
            textAlign: 'center',
            backgroundColor: '#fff',
            padding: '16px',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#333',
              marginBottom: '8px',
            }}
          >
            {t('common.defaultLogo')}:
          </div>

          <div
            style={{
              height: 120,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hasLogo ? 'transparent' : '#f0f0f0',
              marginBottom: 8,
              border: '1px solid #f0f0f0',
              padding: '20px',
            }}
          >
            {hasLogo ? (
              <>
                <img
                  src={loggedInUser.logo.path}
                  alt={loggedInUser.logo.name}
                  style={{
                    height: '100%',
                    objectFit: 'contain',
                    width: '100%',
                  }}
                />
                <DeleteOutlined
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    fontSize: 18,
                    color: 'var(--ant-error-color)',
                    cursor: 'pointer',
                    zIndex: 1,
                  }}
                  onClick={handleRemoveUserLogo}
                />
              </>
            ) : (
              <div
                style={{
                  color: '#aaa',
                  fontSize: 14,
                }}
              >
                {t('common.noLogoAssigned')}
              </div>
            )}
          </div>
        </Card>
      );
    }
    return null;
  };

  let logoList = [];
  if (
    loggedInUser?.role?.name === 'carHouseAdmin' ||
    loggedInUser?.role?.name === 'user'
  ) {
    logoList = carHouseLogos || [];
  } else {
    logoList = logos || [];
  }

  const handleSetUserLogo = async (logoId) => {
    try {
      await assignLogoToUser(loggedInUser.id, logoId);
      message.success(t('common.logoAssignedSuccessfully'));
      dispatch(getSelf(loggedInUser.id, logoId));
    } catch (error) {
      message.error(t('common.failedToAssignLogo'));
    }
  };

  const filteredLogos = logoList.filter((logo) =>
    logo.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedLogos = filteredLogos.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  let content;
  if (
    (loggedInUser?.role?.name !== 'root' && isLoading) ||
    (loggedInUser?.role?.name === 'root' && status === Status.LOADING)
  ) {
    content = <Spinner />;
  } else {
    content = (
      <div className="container-fluid p-0">
        {renderTopLogo()}

        {/* Only show search and logo grid if logos exist */}
        {logoList.length > 0 ? (
          <Card
            title={
              <>
                <h4 style={{textAlign: 'left'}}>{t('common.listOfLogos')}:</h4> 
                <Input.Search
                  placeholder={t('common.searchByName')}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  value={searchTerm}
                  allowClear
                  style={{ maxWidth: 300, textAlign: 'center' }}
                />
              </>
            }
            style={{ marginBottom: 16, textAlign: 'center' }}
          >
            <Row gutter={[16, 16]}>
              {paginatedLogos?.map((logo) => (
                <Col
                  key={logo.id}
                  xs={24}
                  sm={24}
                  md={12}
                  lg={6}
                  xl={6}
                  xxl={4}
                >
                  <Card
                    style={{ height: '100%', position: 'relative' }}
                    title={logo.name}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={logo.path}
                        alt={logo.name}
                        height={150}
                        width={150}
                        style={{ objectFit: 'contain' }}
                      />
                      {loggedInUser?.role?.name !== 'root' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            cursor:
                              loggedInUser.logo?.id === logo.id
                                ? 'not-allowed'
                                : 'pointer',
                          }}
                        >
                          <Tooltip
                            title={
                              loggedInUser.logo?.id === logo.id
                                ? t('common.currentLogo')
                                : t('common.setAsDefaultLogo')
                            }
                          >
                            <Button
                              type={
                                loggedInUser.logo?.id === logo.id
                                  ? 'primary'
                                  : 'default'
                              }
                              disabled={loggedInUser.logo?.id === logo.id}
                              icon={<CheckCircleOutlined />}
                              onClick={() => {
                                if (loggedInUser.logo?.id !== logo.id) {
                                  handleSetUserLogo(logo.id);
                                }
                              }}
                            />
                          </Tooltip>
                        </div>
                      )}

                      {loggedInUser?.role?.name === 'root' && (
                        <DeleteOutlined
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontSize: 20,
                            color: 'var(--ant-error-color)',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            showConfirmDeleteLogo(logo);
                          }}
                        />
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        ) : (
          <Card>
            <div
              style={{ fontSize: '16px', color: '#333', textAlign: 'center' }}
            >
              {t('common.noData')} 
            </div>
          </Card>
        )}

        {logoList.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredLogos.length}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {loggedInUser?.role?.name === 'root' && (
        <PageHeader
          onAdd={() => {
            setOpenModal(true);
            form.resetFields();
          }}
          showButton
          buttonText={t('common.addLogo')} 
        />
      )}
      {content}

      <Modal
        title={t('common.createLogo')} 
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={handleSubmitLogo}
      >
        <Form form={form} layout="vertical" className="text-center">
          <Form.Item
            name="name"
            label={t('common.logoName')}
            rules={[{ required: true, message: t('common.logoNameRequired') }]}>
            <Input placeholder={t('common.enterLogoName')} />
          </Form.Item>

          <Form.Item
            name="logoFile"
            label={t('common.logoFile')}
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            rules={[{ required: true, message: t('common.logoFileRequired') }]}
          >
            <Upload
              name="logo"
              listType="picture"
              maxCount={1}
              beforeUpload={() => false}
              customRequest={({ onSuccess }) =>
                setTimeout(() => onSuccess('ok'), 0)
              }
            >
              <Button icon={<UploadOutlined />}>{t('common.uploadLogo')}</Button> 
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Logos;
